import { Badge, BottomNavigation, BottomNavigationAction, Box, useTheme } from '@mui/material';
import { BaseTheme, mergeSx } from 'design-system';
import { useInputsBuyerInfo } from 'providers/InputsBuyerInfoProvider';
import { useMemo, useState } from 'react';
import { IconType } from 'react-icons';
import { useLocation, useNavigate } from 'react-router';
import { getRoutePrefix } from 'services/theming';
import useButtonColor from 'hooks/useButtonColor';
import { useBuyersMarketCart } from 'hooks/useBuyersMarketCart';
import useDetectKeyboardOpen from 'hooks/useDetectKeyboardOpen';
import useIsDarkMode from 'hooks/useIsDarkMode';
import useNavigationItems from 'hooks/useNavigationItems';
import { useShipmentsOrSalesPath } from 'hooks/useShipmentsOrSalesPath';
import useUser from 'hooks/useUser';
import { ButtonColor } from 'generated/graphql';

const badgeStyle = (theme: BaseTheme) => ({
  '& > svg': {
    fontSize: '1.6em',
  },
  '& > span': {
    height: 20,
    transform: 'scale(1) translate(120%, -30%)',
    background: theme.palette.success.light,
  },
});
const BottomNavCartBadge = ({ icon }: { icon: IconType }) => {
  const { cartGroupCases } = useBuyersMarketCart();

  return (
    <Badge badgeContent={cartGroupCases} sx={badgeStyle}>
      <Box component={icon} sx={{ fontSize: '1.6em' }} />
    </Badge>
  );
};

const BottomNavRequestQuoteBadge = ({ icon }: { icon: IconType }) => {
  const theme = useTheme();
  const { buyerInfo } = useInputsBuyerInfo();
  const color = useMemo(
    () => (buyerInfo?.quotesBadgeStatus.includes('!') ? ButtonColor.Warning : undefined),
    [buyerInfo?.quotesBadgeStatus],
  );
  const badgeColor = useButtonColor(theme, color); // if no color then hook return null

  return (
    <Badge
      badgeContent={buyerInfo?.quotesBadgeStatus}
      sx={mergeSx(badgeStyle, {
        '& > span': {
          transform: 'scale(1) translate(90%, -30%)',
          background: badgeColor ?? theme.palette.success.light,
        },
      })}
    >
      <Box component={icon} sx={{ fontSize: '1.6em' }} />
    </Badge>
  );
};

const BottomNavBadge = ({ icon }: { icon: IconType }) => {
  return (
    <Badge badgeContent={0} sx={badgeStyle}>
      <Box component={icon} sx={{ fontSize: '1.6em' }} />
    </Badge>
  );
};
const BottomNav = () => {
  const isDarkMode = useIsDarkMode();
  const navigate = useNavigate();
  const location = useLocation();

  const prefix = getRoutePrefix();
  const shipmentsOrSalesPath = useShipmentsOrSalesPath();
  const [value, setValue] = useState(location?.pathname ?? shipmentsOrSalesPath);
  const isKeyboardOpen = useDetectKeyboardOpen();

  const tabs = useNavigationItems();
  const { user } = useUser();

  if (isKeyboardOpen) {
    return null;
  }

  return (
    <BottomNavigation
      value={value}
      onChange={(_, route) => {
        setValue(route);
        navigate(route);
      }}
      showLabels
      sx={(theme) => ({
        display: 'none',
        borderBottom: 'none',
        backgroundColor: isDarkMode ? theme.palette.primary.main : theme.palette.common.white,
        [theme.breakpoints.down('md')]: {
          display: 'flex',
        },
        '.Mui-selected': {
          color: isDarkMode ? theme.palette.common.white : theme.palette.primary.main,
        },
      })}
    >
      {tabs.map((tab) => {
        const { label, path, icon: Icon } = tab;

        return (
          <BottomNavigationAction
            key={label}
            label={label}
            value={path}
            sx={(theme) => ({
              color: isDarkMode ? theme.palette.grey[400] : theme.palette.primary.main, // Mobile header uses primary.main for color
            })}
            icon={
              label === 'Cart' && user ? (
                <BottomNavCartBadge icon={Icon} />
              ) : label === 'Request' ? (
                <BottomNavRequestQuoteBadge icon={Icon} />
              ) : (
                <BottomNavBadge icon={Icon} />
              )
            }
          />
        );
      })}
    </BottomNavigation>
  );
};

export default BottomNav;
